import axios from 'axios';
import { API_BASE_URL, DEBUG_MODE } from '../config/apiConfig';

/**
 * MFA Service
 * Handles all Multi-Factor Authentication operations
 */
class MFAService {
    constructor() {
        this.baseURL = API_BASE_URL;
    }

    /**
     * Setup MFA for current user
     * Generates QR code and backup codes
     * @returns {Promise<Object>} Setup response with QR code and backup codes
     */
    async setupMFA() {
        try {
            this.log('Setting up MFA');

            const response = await axios.post(
                `${this.baseURL}/auth/mfa/setup/initiate`,
                {},
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            if (response.status === 200 && response.data) {
                this.log('MFA setup initiated successfully', {
                    hasQRCode: !!response.data.qr_code_url,
                    backupCodesCount: response.data.backup_codes?.length,
                    setupSessionId: response.data.setup_session_id
                });

                return {
                    success: true,
                    message: 'MFA setup initiated successfully',
                    data: {
                        qrCodeUrl: response.data.qr_code_url,
                        secret: response.data.secret_key, // For manual entry (backend uses secret_key)
                        backupCodes: response.data.backup_codes,
                        setupSessionId: response.data.setup_session_id // Required for verification
                    }
                };
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error) {
            this.log('MFA setup failed', {
                error: error.message,
                status: error.response?.status
            });

            const errorMessage = error.response?.data?.error ||
                               error.response?.data?.detail ||
                               error.response?.data?.message ||
                               error.message ||
                               'Failed to setup MFA';

            return {
                success: false,
                message: errorMessage,
                error: error.response?.data
            };
        }
    }

    /**
     * Verify TOTP code during MFA setup
     * @param {string} totpCode - 6-digit TOTP code from authenticator app
     * @param {string} setupSessionId - Setup session ID from initiate response
     * @returns {Promise<Object>} Verification response
     */
    async verifySetup(totpCode, setupSessionId) {
        try {
            this.log('Verifying MFA setup', { totpCode: '******', setupSessionId });

            const response = await axios.post(
                `${this.baseURL}/auth/mfa/setup/verify`,
                {
                    totp_code: totpCode,
                    setup_session_id: setupSessionId
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            if (response.status === 200 && response.data) {
                this.log('MFA setup verified successfully');

                return {
                    success: true,
                    message: response.data.message || 'MFA enabled successfully',
                    data: {
                        ...response.data,
                        backupCodes: response.data.backup_codes || response.data.backupCodes
                    }
                };
            } else {
                throw new Error(response.data?.message || 'Verification failed');
            }
        } catch (error) {
            this.log('MFA setup verification failed', {
                error: error.message,
                status: error.response?.status
            });

            const errorMessage = error.response?.data?.error ||
                               error.response?.data?.detail ||
                               error.response?.data?.message ||
                               error.message ||
                               'Invalid verification code';

            return {
                success: false,
                message: errorMessage,
                error: error.response?.data
            };
        }
    }

    /**
     * Initiate MFA login - validates credentials and checks MFA status
     * @param {string} username - Username or email
     * @param {string} password - User password
     * @returns {Promise<Object>} Initiation response with session ID
     */
    async initiateMFALogin(username, password) {
        try {
            this.log('Initiating MFA login', { username });

            const response = await axios.post(
                `${this.baseURL}/auth/mfa/initiate-login`,
                {
                    username,
                    password
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            if (response.status === 200 && response.data) {
                this.log('MFA login initiated', { sessionId: response.data.temp_token });

                return {
                    success: true,
                    message: response.data.message,
                    data: {
                        sessionId: response.data.temp_token,  // Backend returns temp_token
                        mfaRequired: response.data.mfa_required,
                        method: response.data.mfa_method  // Backend returns mfa_method
                    }
                };
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error) {
            this.log('MFA login initiation failed', { error: error.message });

            const errorMessage = error.response?.data?.error ||
                               error.response?.data?.detail ||
                               error.response?.data?.message ||
                               error.message ||
                               'Failed to initiate MFA login';

            return {
                success: false,
                message: errorMessage,
                error: error.response?.data
            };
        }
    }

    /**
     * Verify TOTP code during login
     * @param {string} sessionId - Session ID from initiate response
     * @param {string} totpCode - 6-digit TOTP code or backup code
     * @returns {Promise<Object>} Verification response
     */
    async verifyLogin(sessionId, totpCode) {
        try {
            this.log('Verifying MFA during login', { sessionId, totpCode: '******' });

            const response = await axios.post(
                `${this.baseURL}/auth/mfa/verify-login`,
                {
                    temp_token: sessionId,
                    mfa_code: totpCode
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            if (response.status === 200 && response.data) {
                this.log('MFA login verification successful');

                return {
                    success: true,
                    message: response.data.message || 'MFA verification successful',
                    data: response.data,
                    isBackupCode: response.data.backup_code_used || false
                };
            } else {
                throw new Error(response.data?.message || 'Verification failed');
            }
        } catch (error) {
            this.log('MFA login verification failed', {
                error: error.message,
                status: error.response?.status
            });

            const errorMessage = error.response?.data?.error ||
                               error.response?.data?.detail ||
                               error.response?.data?.message ||
                               error.message ||
                               'Invalid verification code';

            return {
                success: false,
                message: errorMessage,
                error: error.response?.data
            };
        }
    }

    /**
     * Disable MFA for current user
     * Requires password confirmation
     * @param {string} password - User's current password
     * @returns {Promise<Object>} Disable response
     */
    async disableMFA(password) {
        try {
            this.log('Disabling MFA');

            const response = await axios.post(
                `${this.baseURL}/auth/mfa/disable`,
                {
                    password: password
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            if (response.status === 200 && response.data) {
                this.log('MFA disabled successfully');

                return {
                    success: true,
                    message: response.data.message || 'MFA disabled successfully',
                    data: response.data
                };
            } else {
                throw new Error(response.data?.message || 'Failed to disable MFA');
            }
        } catch (error) {
            this.log('MFA disable failed', {
                error: error.message,
                status: error.response?.status
            });

            const errorMessage = error.response?.data?.error ||
                               error.response?.data?.detail ||
                               error.response?.data?.message ||
                               error.message ||
                               'Failed to disable MFA';

            return {
                success: false,
                message: errorMessage,
                error: error.response?.data
            };
        }
    }

    /**
     * Get MFA status for current user
     * @returns {Promise<Object>} Status response
     */
    async getMFAStatus() {
        try {
            this.log('Getting MFA status');

            const response = await axios.get(
                `${this.baseURL}/auth/mfa/status`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            if (response.status === 200 && response.data) {
                this.log('MFA status retrieved successfully', response.data);

                return {
                    success: true,
                    message: 'Status retrieved successfully',
                    data: {
                        mfaEnabled: response.data.mfa_enabled,
                        mfaMethod: response.data.mfa_method,
                        mfaEnabledAt: response.data.mfa_enabled_at
                    }
                };
            } else {
                throw new Error('Failed to get MFA status');
            }
        } catch (error) {
            this.log('MFA status retrieval failed', {
                error: error.message,
                status: error.response?.status
            });

            const errorMessage = error.response?.data?.error ||
                               error.response?.data?.detail ||
                               error.response?.data?.message ||
                               error.message ||
                               'Failed to get MFA status';

            return {
                success: false,
                message: errorMessage,
                error: error.response?.data
            };
        }
    }

    /**
     * Get backup codes for current user
     * Requires password confirmation
     * @param {string} password - User's current password
     * @returns {Promise<Object>} Backup codes response
     */
    async getBackupCodes(password) {
        try {
            this.log('Getting backup codes status');

            const response = await axios.get(
                `${this.baseURL}/auth/mfa/backup-codes/status`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            if (response.status === 200 && response.data) {
                this.log('Backup codes retrieved successfully', {
                    codesCount: response.data.backup_codes?.length
                });

                return {
                    success: true,
                    message: 'Backup codes retrieved successfully',
                    data: {
                        backupCodes: response.data.backup_codes,
                        usedCodes: response.data.used_codes || []
                    }
                };
            } else {
                throw new Error('Failed to get backup codes');
            }
        } catch (error) {
            this.log('Backup codes retrieval failed', {
                error: error.message,
                status: error.response?.status
            });

            const errorMessage = error.response?.data?.error ||
                               error.response?.data?.detail ||
                               error.response?.data?.message ||
                               error.message ||
                               'Failed to get backup codes';

            return {
                success: false,
                message: errorMessage,
                error: error.response?.data
            };
        }
    }

    /**
     * Regenerate backup codes
     * Invalidates all previous backup codes
     * Requires password confirmation
     * @param {string} password - User's current password
     * @returns {Promise<Object>} New backup codes response
     */
    async regenerateBackupCodes(mfaCode) {
        try {
            this.log('Regenerating backup codes');

            const response = await axios.post(
                `${this.baseURL}/auth/mfa/backup-codes/regenerate`,
                {
                    mfa_code: mfaCode
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            if (response.status === 200 && response.data) {
                this.log('Backup codes regenerated successfully', {
                    codesCount: response.data.backup_codes?.length
                });

                return {
                    success: true,
                    message: response.data.message || 'Backup codes regenerated successfully',
                    data: {
                        backupCodes: response.data.backup_codes
                    }
                };
            } else {
                throw new Error('Failed to regenerate backup codes');
            }
        } catch (error) {
            this.log('Backup codes regeneration failed', {
                error: error.message,
                status: error.response?.status
            });

            const errorMessage = error.response?.data?.error ||
                               error.response?.data?.detail ||
                               error.response?.data?.message ||
                               error.message ||
                               'Failed to regenerate backup codes';

            return {
                success: false,
                message: errorMessage,
                error: error.response?.data
            };
        }
    }

    /**
     * Logging utility
     * @param {string} message - Log message
     * @param {Object} data - Additional data
     */
    log(message, data = null) {
        if (DEBUG_MODE) {
            console.log(`[MFAService] ${message}`, data);
        }
    }
}

// Create singleton instance
const mfaService = new MFAService();

export default mfaService;
